import { SDUIImage } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { numberToPixel } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/conversions";
import {
  DynamicVariable,
  assignInlineVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { getTypographyFontSize } from "@faire/sdui/lib/deprecated/getTypographyFontSize";
import { getColor } from "@faire/sdui/lib/getColor";
import { getIcon } from "@faire/sdui/lib/getIcon";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { IImageOrIcon } from "@faire/web-api--source/indigofair/data/IImageOrIcon";
import { Sizing } from "@faire/web-api--source/indigofair/data/Sizing";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import { cn } from "@faire/web/tools/cn";
import React from "react";

const SIZE_MAP: { [key in Sizing]: number | undefined } = {
  [Sizing.SIZING_UNKNOWN]: undefined,
  [Sizing.SIZING_S]: 16,
  [Sizing.SIZING_M]: 24,
  [Sizing.SIZING_L]: 32,
};

const varHeight: DynamicVariable<"--f-sdui-ioi-h"> = {
  variable: "--f-sdui-ioi-h",
  className: "h-(--f-sdui-ioi-h)",
};

const varWidth: DynamicVariable<"--f-sdui-ioi-w"> = {
  variable: "--f-sdui-ioi-w",
  className: "w-(--f-sdui-ioi-w) min-w-(--f-sdui-ioi-w)",
};

export const SDUIImageOrIconRenderer: React.FC<{
  dataTestId?: string;
  className?: string;
  componentStyle?: React.CSSProperties;
  imageOrIcon: IImageOrIcon;
  parentColorToken?: keyof typeof ColorTokenKey;
  parentFontStyle?: keyof typeof TextStyleType;
}> = ({
  dataTestId,
  className,
  imageOrIcon,
  parentColorToken,
  parentFontStyle,
  componentStyle,
}) => {
  if (imageOrIcon.image) {
    const height = imageOrIcon.image_or_icon_size
      ? SIZE_MAP[imageOrIcon.image_or_icon_size]
      : imageOrIcon.image.height;
    const width = imageOrIcon.image_or_icon_size
      ? SIZE_MAP[imageOrIcon.image_or_icon_size]
      : imageOrIcon.image.width;
    return (
      <div
        data-test-id={dataTestId}
        className={cn(className, varHeight.className, varWidth.className)}
        style={{
          ...assignInlineVars({
            [varHeight.variable]: numberToPixel(height),
            [varWidth.variable]: numberToPixel(width),
          }),
          ...componentStyle,
        }}
      >
        <SDUIImage
          altText={imageOrIcon.image.alt_text}
          responsiveImage={{
            mobileAndAbove: imageOrIcon.image,
          }}
          optimizeImageOptions={{
            mobileAndAbove: {
              height: height,
              width: width,
            },
          }}
        />
      </div>
    );
  }
  if (imageOrIcon.icon) {
    const Icon = getIcon(imageOrIcon.icon);
    const iconSizeString =
      imageOrIcon.image_or_icon_size ?? imageOrIcon.icon.icon_size;

    // if icon size is not provided, default to the parent font size otherwise default to the icon size
    const iconSize = iconSizeString
      ? SIZE_MAP[iconSizeString] + "px"
      : getTypographyFontSize(
          parentFontStyle ?? TextStyleType.TEXT_STYLE_TYPE_UNKNOWN
        );

    return (
      <Icon
        data-test-id={dataTestId}
        fontSize={iconSize}
        height={iconSize}
        width={iconSize}
        className={className}
        style={componentStyle}
        color={getColor(imageOrIcon.icon.color_token ?? parentColorToken)}
      />
    );
  }
  return null;
};
